import { createSelector } from '@reduxjs/toolkit';
import { nonNullable } from '@repo/utils';
import { CHAT_REDUCER_PATH, type RootState } from 'chat/store';
import type { ChatMessage } from 'chat/types/message';
import { MessageDirection } from 'chat/types/message';

const baseSelector = (state: RootState) => state[CHAT_REDUCER_PATH].chatWindow;

const chatInstancesSelector = createSelector(
  baseSelector,
  (state) => state.chatInstances,
);

export const chatInstanceSelector = (chatInstanceId: string) => createSelector(
  chatInstancesSelector,
  (chatInstances) => chatInstances[chatInstanceId],
);

export const chatMessagesSelector = (chatInstanceId: string) => createSelector(
  chatInstanceSelector(chatInstanceId),
  (chatInstance) => chatInstance?.messages ?? [],
);

export const subscriptionIdSelector = (chatInstanceId: string) => createSelector(
  chatInstanceSelector(chatInstanceId),
  (chatInstance) => chatInstance?.subscriptionId ?? null,
);

export const unreadMessageCountSelector = (chatInstanceId: string) => createSelector(
  chatInstanceSelector(chatInstanceId),
  (state) => state?.chatMeta?.unreadMessagesCount ?? 0,
);

export const messagesMetaSelector = (chatInstanceId: string) => createSelector(
  chatInstanceSelector(chatInstanceId),
  (state) => state?.messagesMeta ?? null,
);

export const isCustomerSupportWindowActiveSelector = (chatInstanceId: string) => createSelector(
  chatInstanceSelector(chatInstanceId),
  (state) => state?.chatMeta?.isCustomerSupportWindowActive ?? false,
);

export const lastInboundMessageSelector = (chatInstanceId: string) => createSelector(
  chatMessagesSelector(chatInstanceId),
  (messages) => messages.reduce<ChatMessage | null>((latest, message) => {
    if (message.direction !== MessageDirection.INBOUND) return latest;
    if (!latest || !Number.isInteger(latest.id)) return message;
    return message.id > latest.id ? message : latest;
  }, null),
);

export const didMessagesLoadSelector = (chatInstanceId: string) => createSelector(
  chatInstanceSelector(chatInstanceId),
  // We can assume, that the messages are loaded if the total count is set
  (state) => nonNullable(state?.messagesMeta?.totalCount),
);
